/**
 * MacroRunningIndicator — a small, debug-only "a macro is running" marker.
 *
 * Renders a blue flashing dot fixed to the upper-left corner. It is purely
 * presentational and source-agnostic: callers decide when it is `visible`
 * (typically `debugMode && macroRunning`). When not visible it renders
 * nothing.
 *
 * The dot is non-interactive (`pointer-events: none`, `aria-hidden`) and sits
 * on a very high z-index so it stays on top of gameplay overlays. The flash
 * animation is defined inline so the component is self-contained and works in
 * any app without extra CSS/Tailwind configuration.
 */

export const MACRO_RUNNING_INDICATOR_TESTID = 'macro-running-indicator';

const FLASH_KEYFRAMES = `@keyframes macroRunningFlash {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.25; transform: scale(0.75); }
}`;

export interface MacroRunningIndicatorProps {
  /** When true the dot is shown; when false the component renders nothing. */
  visible: boolean;
}

export const MacroRunningIndicator = ({ visible }: MacroRunningIndicatorProps) => {
  if (!visible) return null;

  return (
    <>
      <style>{FLASH_KEYFRAMES}</style>
      <div
        data-testid={MACRO_RUNNING_INDICATOR_TESTID}
        aria-hidden='true'
        style={{
          position: 'fixed',
          top: 8,
          left: 8,
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: '#2563eb',
          boxShadow: '0 0 6px 2px rgba(37, 99, 235, 0.75)',
          pointerEvents: 'none',
          zIndex: 2147483647,
          animation: 'macroRunningFlash 1s ease-in-out infinite',
        }}
      />
    </>
  );
};
